import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Tag, Tooltip, Typography, App, Spin, Button, Popconfirm } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  instrumentConfigApi,
  InstrumentConfigTransaction,
  InstrumentConfigTransactionListResponse,
  TxnKind,
  TxnStatus,
  TxnType,
} from '../../../services/instrumentConfigApi';
import { formatNumber } from '../../../utils/numberFormat';

const { Text } = Typography;

interface Props {
  portfolioId?: string | null;
  /** Symbol the user is currently focused on. Used to resolve the InstrumentConfig id. */
  symbol?: string | null;
  /** When this changes (or other parent triggers), re-fetch. */
  refreshKey?: number;
  /** Poll in the background for new pending / status transitions. */
  poll?: boolean;
}

const PAGE_SIZE = 20;
const POLL_MS = 15_000;

const formatVND = (n: number): string =>
  formatNumber(n, { fallback: '0' });

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // numberFormat-exempt: date
  return d.toLocaleString('vi-VN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
};

const typeColor: Record<TxnType, string> = {
  DEPOSIT: 'green',
  WITHDRAW: 'red',
};

/** Shown under the type for a line the user did not place themselves. */
const KIND_LABEL: Record<TxnKind, string> = {
  USER: '',
  TRANSFER: 'Moved between legs',
  WRITE_OFF: 'Written off',
  SETTLEMENT: 'Settlement',
  ADJUSTMENT: 'Adjustment',
};

const statusColor: Record<TxnStatus, string> = {
  PENDING: 'gold',
  SUCCESS: 'green',
  FAILED: 'red',
  CANCELLED: 'default',
};

const stripExchange = (s: string): string => (s.includes(':') ? s.split(':').slice(1).join(':') : s);

const CapitalHistoryTab: React.FC<Props> = ({ portfolioId, symbol, refreshKey, poll = true }) => {
  const { message } = App.useApp();
  const [icid, setIcid] = useState<string | null>(null);
  const [data, setData] = useState<InstrumentConfigTransactionListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  // Bumped after a cancel to force an immediate re-fetch (don't wait for the poll).
  const [reloadTick, setReloadTick] = useState(0);
  // Txn id currently being cancelled — drives the per-row button spinner.
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const bareSymbol = useMemo(() => (symbol ? stripExchange(symbol) : null), [symbol]);

  // Resolve instrumentConfigId from (portfolioId, symbol). Re-runs when either changes.
  useEffect(() => {
    setIcid(null);
    setData(null);
    setPage(1);
    if (!portfolioId || !bareSymbol) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await instrumentConfigApi.list({
          portfolio_id: portfolioId,
          symbol: bareSymbol,
          config_type: 'GRID',
          page_size: 1,
        });
        if (cancelled) return;
        setIcid(res.items[0]?.id ?? null);
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          message.error(`Failed to resolve capital config: ${msg}`);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [portfolioId, bareSymbol, refreshKey, message]);

  // Fetch transactions whenever icid / page changes, plus background polling.
  useEffect(() => {
    if (!icid) return;
    let cancelled = false;
    const fetchOnce = async (silent: boolean) => {
      try {
        if (!silent) setLoading(true);
        const res = await instrumentConfigApi.listTransactions(icid, { page, page_size: PAGE_SIZE });
        if (!cancelled) setData(res);
      } catch (e: unknown) {
        if (!silent && !cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          message.error(`Failed to load history: ${msg}`);
        }
      } finally {
        if (!silent && !cancelled) setLoading(false);
      }
    };
    void fetchOnce(false);
    if (!poll) return;
    const id = window.setInterval(() => { void fetchOnce(true); }, POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [icid, page, poll, reloadTick, message]);

  // Cancel a PENDING deposit/withdraw. Only while the bot is stopped: a running bot may
  // already be acting on it, so the server answers 409 and the button is disabled.
  // Unknown counts as running: the server refuses a cancel under a live bot anyway.
  const botActive = data?.botActive !== false;
  const handleCancel = useCallback(
    async (row: InstrumentConfigTransaction) => {
      if (!icid) return;
      setCancellingId(row.id);
      try {
        const res = row.type === 'DEPOSIT'
          ? await instrumentConfigApi.cancelDeposit(icid)
          : await instrumentConfigApi.cancelWithdraw(icid);
        if (res.cancelled) {
          message.success(`${row.type === 'DEPOSIT' ? 'Deposit' : 'Withdraw'} cancelled — no money moved`);
        } else {
          message.info('Nothing to cancel — it has already settled');
        }
        setReloadTick((t) => t + 1);
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        message.error(`Could not cancel: ${err?.response?.data?.message ?? err?.message ?? String(e)}`);
      } finally {
        setCancellingId(null);
      }
    },
    [icid, message],
  );

  const columns: ColumnsType<InstrumentConfigTransaction> = useMemo(() => [
    {
      title: 'Time',
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      width: 170,
      render: (v: string) => <Text style={{ fontSize: 12 }}>{formatDateTime(v)}</Text>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (t: TxnType, row: InstrumentConfigTransaction) => (
        <>
          <Tag color={typeColor[t]}>{t}</Tag>
          {row.kind && row.kind !== 'USER' && (
            <div><Text type="secondary" style={{ fontSize: 11 }}>{KIND_LABEL[row.kind]}</Text></div>
          )}
        </>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (a: string, row: InstrumentConfigTransaction) => (
        <Text style={{ color: row.type === 'DEPOSIT' ? '#0ECB81' : '#F6465D', fontVariantNumeric: 'tabular-nums' }}>
          {row.type === 'DEPOSIT' ? '+' : '−'}{formatVND(Number(a))}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s: TxnStatus, row: InstrumentConfigTransaction) =>
        row.failureReason ? (
          <Tooltip title={row.failureReason}><Tag color={statusColor[s]}>{s}</Tag></Tooltip>
        ) : (
          <Tag color={statusColor[s]}>{s}</Tag>
        ),
    },
    {
      title: 'Completed',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 170,
      render: (v: string | null) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {v ? formatDateTime(v) : '—'}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      align: 'center',
      render: (_: unknown, row: InstrumentConfigTransaction) =>
        row.status === 'PENDING' ? (
          botActive ? (
            <Tooltip title="Stop the bot first — a running bot may already be acting on this.">
              <Button size="small" disabled>
                Cancel
              </Button>
            </Tooltip>
          ) : (
            <Popconfirm
              title={`Cancel this ${row.type === 'DEPOSIT' ? 'deposit' : 'withdraw'}?`}
              description="The bot is stopped. It will be marked CANCELLED and no money moves."
              okText="Cancel it"
              cancelText="Keep"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleCancel(row)}
            >
              <Button danger size="small" loading={cancellingId === row.id}>
                Cancel
              </Button>
            </Popconfirm>
          )
        ) : null,
    },
  ], [handleCancel, cancellingId, botActive]);

  if (!portfolioId || !bareSymbol) {
    return (
      <div style={emptyStyle}>
        Select a portfolio and a symbol to view its capital history.
      </div>
    );
  }
  if (!icid) {
    return (
      <div style={emptyStyle}>
        No instrument config for <b>{bareSymbol}</b> yet — save the grid first to enable deposit / withdraw.
      </div>
    );
  }

  return (
    <div className="capital-history-tab" style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
      <style>{`
        .capital-history-tab .ant-table,
        .capital-history-tab .ant-table-thead > tr > th,
        .capital-history-tab .ant-table-tbody > tr > td,
        .capital-history-tab .ant-table-tbody > tr.ant-table-row > td,
        .capital-history-tab .ant-table-cell,
        .capital-history-tab .ant-table-placeholder,
        .capital-history-tab .ant-table-placeholder > td {
          background: transparent !important;
        }
        .capital-history-tab .ant-table-tbody > tr.ant-table-row:hover > td,
        .capital-history-tab .ant-table-tbody > tr > td.ant-table-cell-row-hover {
          background: rgba(255, 255, 255, 0.03) !important;
        }
      `}</style>
      <div style={summaryStyle}>
        <SummaryItem label="Total deposited" value={data?.totalDeposited} color="#0ECB81" />
        <SummaryItem label="Total withdrawn" value={data?.totalWithdrawn} color="#F6465D" />
        <SummaryItem label="Pending deposit" value={data?.pendingDeposit} color="#F0B90B" />
        <SummaryItem label="Pending withdraw" value={data?.pendingWithdraw} color="#F0B90B" />
        {Number(data?.totalTransferredNet ?? 0) !== 0 && (
          <SummaryItem label="Moved between legs" value={data?.totalTransferredNet} color="#848E9C" />
        )}
      </div>

      <Spin spinning={loading} size="small">
        <Table<InstrumentConfigTransaction>
          rowKey="id"
          columns={columns}
          dataSource={data?.items ?? []}
          size="small"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: data?.total ?? 0,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
            size: 'small',
          }}
          locale={{ emptyText: 'No deposit / withdraw history yet' }}
        />
      </Spin>
    </div>
  );
};

const emptyStyle: React.CSSProperties = {
  padding: '24px',
  textAlign: 'center',
  color: '#848E9C',
  fontSize: 12,
};

const summaryStyle: React.CSSProperties = {
  display: 'grid',
  // Four hard tracks are ~80px each on a phone, too narrow for "Net realized" or
  // "Pending withdraw", so the labels wrapped and the row ran past the screen. auto-fit
  // falls to two per row there and keeps four wherever there is room.
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 45%), 1fr))',
  gap: 8,
};

const SummaryItem: React.FC<{ label: string; value: string | undefined; color: string }> = ({ label, value, color }) => (
  <div style={{ padding: '6px 10px' }}>
    <div style={{ color: '#848E9C', fontSize: 11 }}>{label}</div>
    <div style={{ color, fontVariantNumeric: 'tabular-nums', fontWeight: 600, fontSize: 13 }}>
      {value !== undefined ? formatVND(Number(value)) : '—'}
    </div>
  </div>
);

export default CapitalHistoryTab;
